import React, { useState, useEffect, useRef, useCallback } from 'react';
import io from "socket.io-client";
import axios from 'axios';
import config from '../../../config';
import { toast } from '../../Common/Toast';
import { FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaWindowMinimize, FaWindowMaximize } from 'react-icons/fa';
import { MdCallEnd } from 'react-icons/md';
import { HiOutlineClipboardList } from 'react-icons/hi';
import MilitaryAdminQuestionsView from './MilitaryAdminQuestionsView';
import './MilitarySessionVideoCall.css';
import Swal from 'sweetalert2';

const MilitarySessionVideoCall = ({
  sessionId,
  programId,
  weekNumber,
  userId,
  token,
  user,
  selectedUser,
  isAdmin = false,
  teamName = "",
  onSessionComplete,
  onClose,
  isMinimized = false,
  onToggleMinimize,
  onAbandonSession
}) => {
  // ─── Video/Audio refs ───────────────────────────────────────────────────────
  const localVideoRef  = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerRef        = useRef(null);
  const localStreamRef = useRef(null);
  const socketRef      = useRef(null);

  // ─── Guard refs ─────────────────────────────────────────────────────────────
  const isEndingRef           = useRef(false);
  const hasAbandonedRef       = useRef(false);
  const initialLoadDoneRef    = useRef(false);
  const nextQuestionTimerRef  = useRef(null);
  const cleanupInProgressRef  = useRef(false);

  // ─── Session / question state ───────────────────────────────────────────────
  const [isConnected,       setIsConnected]       = useState(false);
  const [isCallActive,      setIsCallActive]      = useState(false);
  const [error,             setError]             = useState(null);
  const [isMuted,           setIsMuted]           = useState(false);
  const [isVideoOff,        setIsVideoOff]        = useState(false);
  const [hasEnded,          setHasEnded]          = useState(false);

  const [questions,             setQuestions]             = useState([]);
  const [currentQuestionIndex,  setCurrentQuestionIndex]  = useState(0);
  const [currentQuestion,       setCurrentQuestion]       = useState(null);
  const [answeredCount,         setAnsweredCount]         = useState(0);
  const [isSubmitting,          setIsSubmitting]          = useState(false);
  const [showQuestionPanel,     setShowQuestionPanel]     = useState(false);
  const [sessionCompleted,      setSessionCompleted]      = useState(false);
  const [sessionResults,        setSessionResults]        = useState(null);
  const [isLoading,             setIsLoading]             = useState(true);
  const [showAdminQuestions,    setShowAdminQuestions]    = useState(false);

  const [waitingForNext,    setWaitingForNext]    = useState(false);
  const [nextQuestionTime,  setNextQuestionTime]  = useState(null);
  const [waitTimeRemaining, setWaitTimeRemaining] = useState(0);

  const [answerTimer, setAnswerTimer] = useState(120); // 2 minutes in seconds
  const [timerActive, setTimerActive] = useState(false);
  const answerTimerRef = useRef(null);

  // ─── Reattach streams after minimize/maximize ───────────────────────────────
  useEffect(() => {
    if (localStreamRef.current && localVideoRef.current &&
        localVideoRef.current.srcObject !== localStreamRef.current) {
      localVideoRef.current.srcObject = localStreamRef.current;
    }
  }, [isMinimized]);

  useEffect(() => {
    if (remoteVideoRef.current && peerRef.current) {
      const remoteStream = peerRef.current.getRemoteStreams?.()?.[0];
      if (remoteStream && remoteVideoRef.current.srcObject !== remoteStream) {
        remoteVideoRef.current.srcObject = remoteStream;
      }
    }
  }, [isMinimized]);

  // ─── Between-question countdown (2 minutes) ─────────────────────────────────
  useEffect(() => {
    if (!waitingForNext || !nextQuestionTime) return;

    const tick = () => {
      const remaining = Math.max(0, Math.floor((nextQuestionTime - Date.now()) / 1000));
      setWaitTimeRemaining(remaining);
      if (remaining <= 0) {
        if (nextQuestionTimerRef.current) {
          clearInterval(nextQuestionTimerRef.current);
          nextQuestionTimerRef.current = null;
        }
        if (!hasEnded && !sessionCompleted) {
          setWaitingForNext(false);
          setNextQuestionTime(null);
          showNextQuestion();
        }
      }
    };

    tick();
    nextQuestionTimerRef.current = setInterval(tick, 1000);
    return () => {
      if (nextQuestionTimerRef.current) {
        clearInterval(nextQuestionTimerRef.current);
        nextQuestionTimerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingForNext, nextQuestionTime]);

  // ─── Load session on mount ──────────────────────────────────────────────────
  useEffect(() => {
    if (sessionId) loadSessionDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // ─── Show first question once loading is done (candidate only) ──────────────
  useEffect(() => {
    if (isAdmin || isLoading || questions.length === 0 ||
        initialLoadDoneRef.current || sessionCompleted || hasEnded) return;

    initialLoadDoneRef.current = true;
    const t = setTimeout(() => {
      if (hasEnded || sessionCompleted) return;
      const firstIdx = questions.findIndex(q => !q.answered);
      if (firstIdx !== -1) {
        setCurrentQuestionIndex(firstIdx);
        setCurrentQuestion(questions[firstIdx]);
        setShowQuestionPanel(true);
        setWaitingForNext(false);
      }
    }, 1000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isLoading, questions, sessionCompleted]);

  // ─── Socket setup ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!sessionId || !userId || hasEnded) return;

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    socketRef.current = io(config.baseURL, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
    });

    socketRef.current.on("connect", () => {
      setIsConnected(true);
      startCall();
    });

    socketRef.current.on("connect_error", (err) => {
      setError(`Connection failed: ${err.message}`);
    });

    socketRef.current.on("military-user-disconnected", () => {
      if (hasEnded) return;
      toast.info(`${isAdmin ? 'Candidate' : 'Interviewer'} has left the session`);
      
      if (isAdmin) {
        handleAdminEndSessionWithOptions();
      } else {
        setTimeout(() => {
          cleanupMediaAndPeer();
          onClose?.();
        }, 2000);
      }
    });

    setupSocketListeners(socketRef.current);

    return () => {
      if (socketRef.current && !hasEnded) {
        socketRef.current.emit("leave-military-room", { sessionId });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, userId, hasEnded]);

  const handleAdminEndSessionWithOptions = async () => {
    if (isEndingRef.current || hasEnded) return;
    
    const result = await Swal.fire({
      title: 'End Assessment Session?',
      text: `Candidate has left. How would you like to proceed with ${selectedUser?.first_name || 'the candidate'}?`,
      icon: 'question',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonColor: '#10b981',
      denyButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: '✅ Accept',
      denyButtonText: '❌ Reject',
      cancelButtonText: '⏳ Pending',
      allowOutsideClick: false
    });

    try {
      if (result.isConfirmed) {
        await handleAdminAcceptCandidate();
      } else if (result.isDenied) {
        await handleAdminRejectCandidate();
      } else if (result.isDismissed) {
        await handleAdminPendingCandidate();
      }
    } catch (error) {
      console.error('Error handling admin decision:', error);
      toast.error('Failed to process decision');
    } finally {
      await cleanupAndClose(true);
      onClose?.();
    }
  };

  const handleAdminAcceptCandidate = async () => {
    try {
      const response = await axios.post(
        `${config.baseURL}/api/military/sessions/${sessionId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`${selectedUser?.first_name || 'Candidate'} has been accepted and session completed!`);
        onSessionComplete?.(response.data);
      }
    } catch (error) {
      console.error('Error completing session:', error);
      toast.error('Failed to complete session');
      throw error;
    }
  };

  const handleAdminRejectCandidate = async () => {
    try {
      await axios.post(
        `${config.baseURL}/api/military/sessions/${sessionId}/abandon`,
        { status: 'rejected', action: 'reject' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (programId) {
        await axios.patch(
          `${config.baseURL}/api/military/programs/${programId}/candidate-status`,
          { status: 'rejected' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.info(`${selectedUser?.first_name || 'Candidate'} has been rejected`);
    } catch (error) {
      console.error('Error rejecting candidate:', error);
      toast.error('Failed to reject candidate');
      throw error;
    }
  };

  const handleAdminPendingCandidate = async () => {
    try {
      await axios.post(
        `${config.baseURL}/api/military/sessions/${sessionId}/abandon`,
        { status: 'pending', action: 'abandon' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.info(`${selectedUser?.first_name || 'Candidate'} marked as pending - session can be resumed later`);
    } catch (error) {
      console.error('Error marking as pending:', error);
      toast.error('Failed to mark as pending');
      throw error;
    }
  };

  const handleEndSession = async () => {
    if (isEndingRef.current || hasEnded) return;

    if (isAdmin) {
      const result = await Swal.fire({
        title: 'End Assessment Session?',
        text: `How would you like to proceed with ${selectedUser?.first_name || 'the candidate'}?`,
        icon: 'question',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonColor: '#10b981',
        denyButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: '✅ Accept',
        denyButtonText: '❌ Reject',
        cancelButtonText: '⏳ Pending',
        allowOutsideClick: false
      });

      try {
        if (result.isConfirmed) {
          await handleAdminAcceptCandidate();
        } else if (result.isDenied) {
          await handleAdminRejectCandidate();
        } else if (result.isDismissed) {
          await handleAdminPendingCandidate();
        }
      } catch (error) {
        console.error('Error handling admin decision:', error);
      } finally {
        await cleanupAndClose(true);
        onClose?.();
      }
    } else {
      if (onAbandonSession) {
        try {
          await onAbandonSession(sessionId);
        } catch (_) {}
      }
      await cleanupAndClose(false);
      onClose?.();
    }
  };

  // ─── Join military socket room ──────────────────────────────────────────────
  useEffect(() => {
    if (!socketRef.current || !sessionId) return;
    const join = () => socketRef.current?.emit("join-military-room", {
      sessionId,
      userType: isAdmin ? "admin" : "candidate"
    });
    socketRef.current.connected ? join() : socketRef.current.on("connect", join);
    return () => {
      socketRef.current?.emit("leave-military-room", { sessionId });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isAdmin]);

  // ─── Listen for question-answered events ────────────────────────────────────
  useEffect(() => {
    if (!socketRef.current) return;
    const handler = (data) => {
      if (String(data.sessionId) === String(sessionId)) {
        setAnsweredCount(data.questionNumber);
        loadSessionDetails();
      }
    };
    socketRef.current.on("military-question-answered", handler);
    return () => socketRef.current?.off("military-question-answered", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // ─── Full cleanup on component unmount ──────────────────────────────────────
  useEffect(() => {
    return () => {
      if (!hasEnded && !sessionCompleted && !hasAbandonedRef.current) {
        callAbandonAPI(sessionId);
      }

      if (nextQuestionTimerRef.current) {
        clearInterval(nextQuestionTimerRef.current);
        nextQuestionTimerRef.current = null;
      }

      cleanupMediaAndPeer();

      if (socketRef.current) {
        socketRef.current.emit("leave-military-video", { sessionId: String(sessionId) });
        socketRef.current.emit("leave-military-room", { sessionId });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  /** Complete cleanup of all media resources and peer connection */
  const cleanupMediaAndPeer = () => {
    if (cleanupInProgressRef.current) return;
    cleanupInProgressRef.current = true;

    // Clear all timers
    if (nextQuestionTimerRef.current) {
      clearInterval(nextQuestionTimerRef.current);
      nextQuestionTimerRef.current = null;
    }

    // Stop and clear local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        track.stop();
        track.enabled = false;
      });
      localStreamRef.current = null;
    }

    // Clear video elements
    if (localVideoRef.current) {
      if (localVideoRef.current.srcObject) {
        localVideoRef.current.srcObject.getTracks?.().forEach(track => track.stop());
        localVideoRef.current.srcObject = null;
      }
    }
    
    if (remoteVideoRef.current) {
      if (remoteVideoRef.current.srcObject) {
        remoteVideoRef.current.srcObject.getTracks?.().forEach(track => track.stop());
        remoteVideoRef.current.srcObject = null;
      }
    }

    // Close peer connection
    if (peerRef.current) {
      try {
        peerRef.current.getTransceivers?.().forEach(t => {
          try { t.stop?.(); } catch (_) {}
        });
        peerRef.current.close();
      } catch (_) {}
      peerRef.current = null;
    }

    cleanupInProgressRef.current = false;
  };

  /** Call the abandon API exactly once */
  const callAbandonAPI = async (sid) => {
    if (hasAbandonedRef.current || !sid) return;
    hasAbandonedRef.current = true;
    try {
      await axios.post(
        `${config.baseURL}/api/military/sessions/${sid}/abandon`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error('callAbandonAPI failed:', err);
    }
  };

  /** Single exit point for all close paths */
  const cleanupAndClose = useCallback(async (skipAbandon = false) => {
    if (isEndingRef.current) return;
    isEndingRef.current = true;

    if (!skipAbandon && !sessionCompleted && !hasAbandonedRef.current) {
      await callAbandonAPI(sessionId);
    }

    setShowQuestionPanel(false);
    setWaitingForNext(false);
    setCurrentQuestion(null);

    cleanupMediaAndPeer();

    if (socketRef.current) {
      socketRef.current.emit("leave-military-video", { sessionId: String(sessionId) });
      socketRef.current.emit("leave-military-room", { sessionId });
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setHasEnded(true);
    setIsCallActive(false);
    setIsConnected(false);
  }, [sessionId, sessionCompleted]);

  // ─── Data loading ───────────────────────────────────────────────────────────
  const loadSessionDetails = async () => {
    try {
      const response = await axios.get(
        `${config.baseURL}/api/military/sessions/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        const sd = response.data.session;
        const sortedQuestions = [...sd.questions].sort((a, b) => a.id - b.id);
        
        setQuestions(sortedQuestions);
        setAnsweredCount(sd.answered_count);

        if (!waitingForNext && !isAdmin) {
          const firstUnansweredIdx = sortedQuestions.findIndex(q => !q.answered);
          if (firstUnansweredIdx !== -1) {
            setCurrentQuestionIndex(firstUnansweredIdx);
            setCurrentQuestion(sortedQuestions[firstUnansweredIdx]);
            setShowQuestionPanel(true);
          } else if (sd.answered_count === sd.total_questions) {
            setSessionCompleted(true);
            setShowQuestionPanel(false);
          }
        }
      }
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Failed to load session questions');
    } finally {
      setIsLoading(false);
    }
  };

  const showNextQuestion = () => {
    const nextIdx = currentQuestionIndex + 1;
    if (nextIdx < questions.length) {
      setCurrentQuestionIndex(nextIdx);
      setCurrentQuestion(questions[nextIdx]);
      setShowQuestionPanel(true);
      setWaitingForNext(false);
      setNextQuestionTime(null);
    } else {
      completeSession();
    }
  };

  // ─── WebRTC ─────────────────────────────────────────────────────────────────
  const startCall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      localStreamRef.current = stream;

      socketRef.current.emit("join-military-video", { sessionId: String(sessionId) });
      createPeerConnection();
      stream.getTracks().forEach(track => peerRef.current?.addTrack(track, stream));

      setTimeout(async () => {
        if (peerRef.current?.signalingState === "stable") {
          const offer = await peerRef.current.createOffer();
          await peerRef.current.setLocalDescription(offer);
          socketRef.current.emit("military-video-offer", { sessionId: String(sessionId), offer });
          setIsCallActive(true);
        }
      }, 1000);
    } catch (err) {
      setError(`Failed to access camera/microphone: ${err.message}`);
    }
  };

  const createPeerConnection = () => {
    peerRef.current = new RTCPeerConnection({
      iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
        { urls: "stun:stun2.l.google.com:19302" },
      ],
    });

    peerRef.current.onicecandidate = (event) => {
      if (event.candidate) {
        socketRef.current?.emit("military-ice-candidate", {
          sessionId: String(sessionId),
          candidate: event.candidate
        });
      }
    };

    peerRef.current.ontrack = (event) => {
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
    };
  };

  const setupSocketListeners = (socket) => {
    socket.on("military-video-offer", async ({ offer }) => {
      if (!peerRef.current) createPeerConnection();
      await peerRef.current.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await peerRef.current.createAnswer();
      await peerRef.current.setLocalDescription(answer);
      socket.emit("military-video-answer", { sessionId: String(sessionId), answer });
      setIsCallActive(true);
    });

    socket.on("military-video-answer", async ({ answer }) => {
      if (peerRef.current && peerRef.current.remoteDescription === null) {
        await peerRef.current.setRemoteDescription(new RTCSessionDescription(answer));
      }
    });

    socket.on("military-ice-candidate", async ({ candidate }) => {
      if (peerRef.current && candidate) {
        await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      }
    });
  };

  // ─── Answer submission (verbal only - no text input) ────────────────────────
  const handleSubmitAnswer = async () => {
    if (isSubmitting) return;
    setTimerActive(false);
    if (answerTimerRef.current) {
      clearInterval(answerTimerRef.current);
    }
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${config.baseURL}/api/military/sessions/${sessionId}/submit-response`,
        {
          question_id: currentQuestion.id,
          answer_text: "[VERBAL RESPONSE - RECORDED VIA VIDEO]",
          video_url: null,
          reaction_time: null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const newCount = answeredCount + 1;
        setAnsweredCount(newCount);
        setShowQuestionPanel(false);

        socketRef.current?.emit("military-question-answered", {
          sessionId: String(sessionId),
          questionId: currentQuestion.id,
          questionNumber: newCount,
          totalQuestions: questions.length
        });

        socketRef.current?.emit("military-answer-update", {
          sessionId: String(sessionId),
          questionId: currentQuestion.id,
          answerText: "[VERBAL RESPONSE - RECORDED VIA VIDEO]",
          questionNumber: newCount,
          totalQuestions: questions.length
        });

        const nextIdx = currentQuestionIndex + 1;
        if (nextIdx < questions.length) {
          toast.success(`Question ${newCount} of ${questions.length} completed`);
          setCurrentQuestionIndex(nextIdx);
          setCurrentQuestion(questions[nextIdx]);
          setWaitingForNext(true);
          setNextQuestionTime(Date.now() + 120000); // 2 minute wait before next question
        } else {
          await completeSession();
        }
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast.error('Failed to submit answer');
      setShowQuestionPanel(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Session completion ─────────────────────────────────────────────────────
  const completeSession = async () => {
    try {
      const response = await axios.post(
        `${config.baseURL}/api/military/sessions/${sessionId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        hasAbandonedRef.current = true;
        setSessionCompleted(true);
        setSessionResults(response.data);
        setShowQuestionPanel(false);
        setWaitingForNext(false);

        if (response.data.program_completed) {
          toast.success('🎉 Assessment Program Completed!');
        } else {
          toast.success(`Week ${weekNumber} completed!`);
        }
        onSessionComplete?.(response.data);
      }
    } catch (error) {
      console.error('Error completing session:', error);
      toast.error('Failed to complete session');
    }
  };

  // ─── Audio / video toggles ──────────────────────────────────────────────────
  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTracks = localStreamRef.current.getAudioTracks();
      audioTracks.forEach(track => { track.enabled = !track.enabled; });
      setIsMuted(prev => !prev);
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTracks = localStreamRef.current.getVideoTracks();
      videoTracks.forEach(track => { track.enabled = !track.enabled; });
      setIsVideoOff(prev => !prev);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Add timer effect
useEffect(() => {
  if (timerActive && answerTimer > 0) {
    answerTimerRef.current = setInterval(() => {
      setAnswerTimer(prev => {
        if (prev <= 1) {
          // Auto-submit when timer reaches 0
          handleSubmitAnswer();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  } else if (answerTimer === 0) {
    clearInterval(answerTimerRef.current);
  }

  return () => {
    if (answerTimerRef.current) {
      clearInterval(answerTimerRef.current);
    }
  };
}, [timerActive, answerTimer]);

useEffect(() => {
  if (showQuestionPanel && currentQuestion && !isAdmin) {
    setAnswerTimer(120);
    setTimerActive(true);
  } else {
    setTimerActive(false);
    if (answerTimerRef.current) {
      clearInterval(answerTimerRef.current);
    }
  }
}, [showQuestionPanel, currentQuestion]);

  // ─── Early returns ──────────────────────────────────────────────────────────
  if (hasEnded) return null;

  if (error) {
    return (
      <div className="military_video-call-error">
        <p>{error}</p>
        <button onClick={handleEndSession}>Close</button>
      </div>
    );
  }

  // ─── Minimized view ─────────────────────────────────────────────────────────
  if (isMinimized) {
    return (
      <div className="military_video-call-minimized">
        <div className="military_minimized-header">
          <span className="military_minimized-title">Week {weekNumber} Assessment</span>
          <button className="military_minimized-maximize-btn" onClick={onToggleMinimize}>
            <FaWindowMaximize />
          </button>
        </div>
        <div className="military_minimized-video">
          <video ref={localVideoRef} autoPlay muted playsInline />
          <div className="military_minimized-label">
            You {waitingForNext && <span className="military_waiting-indicator"> ⏳</span>}
          </div>
        </div>
      </div>
    );
  }

  // ─── Admin view ─────────────────────────────────────────────────────────────
  if (isAdmin) {
    return (
      <div className="military_video-call-container">
        <div className="military_video-call-header">
          <div className="military_header-title">
            Week {weekNumber} Assessment: {selectedUser?.first_name || 'Candidate'}
          </div>
          <div className="military_header-actions">
            {/* Add Back button for admin to return to batch manager */}
            {/* <button 
              className="military_back-btn" 
              onClick={() => {
                if (window.confirm('Return to candidate list? Your session will remain active.')) {
                  onClose?.();
                }
              }}
              title="Back to candidates"
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '20px',
                cursor: 'pointer',
                marginRight: '12px',
                padding: '4px 8px'
              }}
            >
              ← Back
            </button> */}
            <button className="military_minimize-btn" onClick={() => {
                if (window.confirm('Return to candidate list? Your session will remain active.')) {
                  onClose?.();
                }
              }}>
              <FaWindowMinimize />
            </button>
            {/* <button className="military_minimize-btn" onClick={onToggleMinimize}>
              <FaWindowMinimize />
            </button> */}
          </div>
        </div>

        <div className="military_video-grid">
          <div className="military_video-local">
            <video ref={localVideoRef} autoPlay muted playsInline />
            <div className="military_video-label">You</div>
          </div>
          <div className="military_video-remote">
            <video ref={remoteVideoRef} autoPlay playsInline />
            <div className="military_video-label">{selectedUser?.first_name || 'Candidate'}</div>
          </div>
        </div>

        <div className="military_video-controls">
          <button onClick={toggleMute} className={`military_control-btn ${isMuted ? 'military_active' : ''}`}>
            {isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
          </button>
          <button onClick={toggleVideo} className={`military_control-btn ${isVideoOff ? 'military_active' : ''}`}>
            {isVideoOff ? <FaVideoSlash /> : <FaVideo />}
          </button>
          <button onClick={() => setShowAdminQuestions(true)} className="military_control-btn military_questions-btn">
            <HiOutlineClipboardList />
          </button>
          <button onClick={handleEndSession} className="military_control-btn military_end-btn">
            <MdCallEnd />
          </button>
        </div>

        {showAdminQuestions && (
          <MilitaryAdminQuestionsView
            sessionId={sessionId}
            token={token}
            weekNumber={weekNumber}
            candidateName={selectedUser?.first_name}
            onClose={() => setShowAdminQuestions(false)}
          />
        )}
      </div>
    );
  }

  // ─── Candidate loading view ─────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="military_video-call-container">
        <div className="military_video-call-header">
          <div className="military_header-title">Week {weekNumber} Assessment</div>
          <div className="military_header-actions">
            <button className="military_minimize-btn" onClick={onToggleMinimize}>
              <FaWindowMinimize />
            </button>
          </div>
        </div>
        <div className="military_video-grid">
          <div className="military_video-local">
            <video ref={localVideoRef} autoPlay muted playsInline />
            <div className="military_video-label">You</div>
          </div>
          <div className="military_video-remote">
            <video ref={remoteVideoRef} autoPlay playsInline />
            <div className="military_video-label">Interviewer</div>
          </div>
        </div>
        <div className="military_loading-overlay">
          <div className="military_loading-spinner"></div>
          <h3>Preparing your assessment session...</h3>
        </div>
      </div>
    );
  }

  // ─── Candidate completed view ───────────────────────────────────────────────
  if (sessionCompleted) {
    return (
      <div className="military_video-call-container">
        <div className="military_video-call-header">
          <div className="military_header-title">Week {weekNumber} Assessment</div>
          <div className="military_header-actions">
            <button className="military_minimize-btn" onClick={onToggleMinimize}>
              <FaWindowMinimize />
            </button>
          </div>
        </div>
        <div className="military_video-grid">
          <div className="military_video-local">
            <video ref={localVideoRef} autoPlay muted playsInline />
            <div className="military_video-label">You</div>
          </div>
          <div className="military_video-remote">
            <video ref={remoteVideoRef} autoPlay playsInline />
            <div className="military_video-label">Interviewer</div>
          </div>
        </div>
        <div className="military_completion-panel">
          <div className="military_completion-icon">✅</div>
          <h3>Week {weekNumber} Complete!</h3>
          <p>You've successfully completed this week's assessment.</p>
          {sessionResults?.program_completed ? (
            <div className="military_program-complete">
              <h4>🎉 Program Completed!</h4>
              <p>Your full assessment results are ready.</p>
            </div>
          ) : (
            <p>Next session will be available in 7 days.</p>
          )}
          <button
            className="military_close-btn"
            onClick={async () => {
              await cleanupAndClose(true);
              onClose?.();
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // ─── Candidate active view (verbal-only interview) ──────────────────────────
  return (
    <div className="military_video-call-container">
      <div className="military_video-call-header">
        <div className="military_header-title">Week {weekNumber} Assessment</div>
        <div className="military_header-actions">
          <button className="military_minimize-btn" onClick={onToggleMinimize}>
            <FaWindowMinimize />
          </button>
        </div>
      </div>

      <div className="military_video-grid">
        <div className="military_video-local">
          <video ref={localVideoRef} autoPlay muted playsInline />
          <div className="military_video-label">
            You
            {isMuted    && <FaMicrophoneSlash className="military_status-icon" />}
            {isVideoOff && <FaVideoSlash      className="military_status-icon" />}
          </div>
        </div>
        <div className="military_video-remote">
          <video ref={remoteVideoRef} autoPlay playsInline />
          <div className="military_video-label">Interviewer</div>
        </div>
      </div>

      {/* Question panel - verbal only, no textarea */}
      {showQuestionPanel && currentQuestion && !waitingForNext && !sessionCompleted && (
        <div className="military_question-panel">
          <div className="military_question-header">
            <div className="military_question-progress">
              Question {currentQuestionIndex + 1} of {questions.length}
            </div>
            <div className={`military_timer ${answerTimer <= 30 ? 'warning' : ''}`}>
              ⏱️ {Math.floor(answerTimer / 60)}:{(answerTimer % 60).toString().padStart(2, '0')}
            </div>
          </div>
          <div className="military_question-content">
            <div className="military_question-category">{currentQuestion.category}</div>
            <div className="military_question-text">{currentQuestion.text}</div>
          </div>
          <div className="military_answer-section">
            <div className="military_action-buttons">
              <button
                className="military_skip-btn"
                onClick={() => handleSubmitAnswer('[SKIPPED]')}
                disabled={isSubmitting}
              >
                Skip
              </button>
              <button
                className="military_submit-btn"
                onClick={() => handleSubmitAnswer()}
                // disabled={isSubmitting || !answerText.trim()}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Answer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Waiting panel - 2 minute break between questions */}
      {waitingForNext && nextQuestionTime && !showQuestionPanel && !sessionCompleted && (
        <div className="military_waiting-panel">
          <div className="military_waiting-icon">⏳</div>
          <h3>Next Question in {formatTime(waitTimeRemaining)}</h3>
          <p>Question {currentQuestionIndex + 1} of {questions.length}</p>
          <div className="military_waiting-progress">
            <div
              className="military_waiting-progress-fill"
              style={{ width: `${((120 - waitTimeRemaining) / 120) * 100}%` }}
            />
          </div>
          <p className="military_waiting-hint">Take a moment to prepare for the next question</p>
        </div>
      )}

      <div className="military_video-controls">
        <button onClick={toggleMute} className={`military_control-btn ${isMuted ? 'military_active' : ''}`}>
          {isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
        </button>
        <button onClick={toggleVideo} className={`military_control-btn ${isVideoOff ? 'military_active' : ''}`}>
          {isVideoOff ? <FaVideoSlash /> : <FaVideo />}
        </button>
        <button onClick={handleEndSession} className="military_control-btn military_end-btn">
          <MdCallEnd />
        </button>
      </div>
    </div>
  );
};

export default MilitarySessionVideoCall;





// import React, { useState, useEffect, useRef, useCallback } from 'react';
// import io from "socket.io-client";
// import axios from 'axios';
// import config from '../../config';
// import { toast } from '../../components/Common/Toast';
// import { FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaWindowMinimize, FaWindowMaximize } from 'react-icons/fa';
// import { MdCallEnd } from 'react-icons/md';
// import { HiOutlineClipboardList } from 'react-icons/hi';
// import MilitaryAdminQuestionsView from './MilitaryAdminQuestionsView';
// import './MilitarySessionVideoCall.css';
// import Swal from 'sweetalert2';

// const MilitarySessionVideoCall = ({
//   sessionId,
//   programId,
//   weekNumber,
//   userId,
//   token,
//   user,
//   selectedUser,
//   isAdmin = false,
//   teamName = "",
//   onSessionComplete,
//   onClose,
//   isMinimized = false,
//   onToggleMinimize,
//   onAbandonSession
// }) => {
//   // ─── Video/Audio refs ───────────────────────────────────────────────────────
//   const localVideoRef  = useRef(null);
//   const remoteVideoRef = useRef(null);
//   const peerRef        = useRef(null);
//   const localStreamRef = useRef(null);
//   const socketRef      = useRef(null);

//   // ─── Guard refs ─────────────────────────────────────────────────────────────
//   const isEndingRef           = useRef(false);
//   const hasAbandonedRef       = useRef(false);
//   const initialLoadDoneRef    = useRef(false);
//   const nextQuestionTimerRef  = useRef(null);
//   const cleanupInProgressRef  = useRef(false);

//   // ─── Session / question state ───────────────────────────────────────────────
//   const [isConnected,       setIsConnected]       = useState(false);
//   const [isCallActive,      setIsCallActive]      = useState(false);
//   const [error,             setError]             = useState(null);
//   const [isMuted,           setIsMuted]           = useState(false);
//   const [isVideoOff,        setIsVideoOff]        = useState(false);
//   const [hasEnded,          setHasEnded]          = useState(false);

//   const [questions,             setQuestions]             = useState([]);
//   const [currentQuestionIndex,  setCurrentQuestionIndex]  = useState(0);
//   const [currentQuestion,       setCurrentQuestion]       = useState(null);
//   const [answeredCount,         setAnsweredCount]         = useState(0);
//   const [isSubmitting,          setIsSubmitting]          = useState(false);
//   const [showQuestionPanel,     setShowQuestionPanel]     = useState(false);
//   const [sessionCompleted,      setSessionCompleted]      = useState(false);
//   const [sessionResults,        setSessionResults]        = useState(null);
//   const [isLoading,             setIsLoading]             = useState(true);
//   const [showAdminQuestions,    setShowAdminQuestions]    = useState(false);

//   const [waitingForNext,    setWaitingForNext]    = useState(false);
//   const [nextQuestionTime,  setNextQuestionTime]  = useState(null);
//   const [waitTimeRemaining, setWaitTimeRemaining] = useState(0);

//   // ─── Reattach streams after minimize/maximize ───────────────────────────────
//   useEffect(() => {
//     if (localStreamRef.current && localVideoRef.current &&
//         localVideoRef.current.srcObject !== localStreamRef.current) {
//       localVideoRef.current.srcObject = localStreamRef.current;
//     }
//   }, [isMinimized]);

//   useEffect(() => {
//     if (remoteVideoRef.current && peerRef.current) {
//       const remoteStream = peerRef.current.getRemoteStreams?.()?.[0];
//       if (remoteStream && remoteVideoRef.current.srcObject !== remoteStream) {
//         remoteVideoRef.current.srcObject = remoteStream;
//       }
//     }
//   }, [isMinimized]);

//   // ─── Between-question countdown (2 minutes) ─────────────────────────────────
//   useEffect(() => {
//     if (!waitingForNext || !nextQuestionTime) return;

//     const tick = () => {
//       const remaining = Math.max(0, Math.floor((nextQuestionTime - Date.now()) / 1000));
//       setWaitTimeRemaining(remaining);
//       if (remaining <= 0) {
//         if (nextQuestionTimerRef.current) {
//           clearInterval(nextQuestionTimerRef.current);
//           nextQuestionTimerRef.current = null;
//         }
//         if (!hasEnded && !sessionCompleted) {
//           setWaitingForNext(false);
//           setNextQuestionTime(null);
//           showNextQuestion();
//         }
//       }
//     };

//     tick();
//     nextQuestionTimerRef.current = setInterval(tick, 1000);
//     return () => {
//       if (nextQuestionTimerRef.current) {
//         clearInterval(nextQuestionTimerRef.current);
//         nextQuestionTimerRef.current = null;
//       }
//     };
//   // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [waitingForNext, nextQuestionTime]);

//   // ─── Load session on mount ──────────────────────────────────────────────────
//   useEffect(() => {
//     if (sessionId) loadSessionDetails();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [sessionId]);

//   // ─── Show first question once loading is done (candidate only) ──────────────
//   useEffect(() => {
//     if (isAdmin || isLoading || questions.length === 0 ||
//         initialLoadDoneRef.current || sessionCompleted || hasEnded) return;

//     initialLoadDoneRef.current = true;
//     const t = setTimeout(() => {
//       if (hasEnded || sessionCompleted) return;
//       const firstIdx = questions.findIndex(q => !q.answered);
//       if (firstIdx !== -1) {
//         setCurrentQuestionIndex(firstIdx);
//         setCurrentQuestion(questions[firstIdx]);
//         setShowQuestionPanel(true);
//         setWaitingForNext(false);
//       }
//     }, 1000);
//     return () => clearTimeout(t);
//   // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [isAdmin, isLoading, questions, sessionCompleted]);

//   // ─── Socket setup ───────────────────────────────────────────────────────────
//   useEffect(() => {
//     if (!sessionId || !userId || hasEnded) return;

//     if (socketRef.current) {
//       socketRef.current.disconnect();
//       socketRef.current = null;
//     }

//     socketRef.current = io(config.baseURL, {
//       auth: { token },
//       transports: ["websocket", "polling"],
//       reconnection: true,
//     });

//     socketRef.current.on("connect", () => {
//       setIsConnected(true);
//       startCall();
//     });

//     socketRef.current.on("connect_error", (err) => {
//       setError(`Connection failed: ${err.message}`);
//     });

//     socketRef.current.on("military-user-disconnected", () => {
//       if (hasEnded) return;
//       toast.info(`${isAdmin ? 'Candidate' : 'Interviewer'} has left the session`);
      
//       if (isAdmin) {
//         handleAdminEndSessionWithOptions();
//       } else {
//         setTimeout(() => {
//           cleanupMediaAndPeer();
//           onClose?.();
//         }, 2000);
//       }
//     });

//     setupSocketListeners(socketRef.current);

//     return () => {
//       if (socketRef.current && !hasEnded) {
//         socketRef.current.emit("leave-military-room", { sessionId });
//         socketRef.current.disconnect();
//         socketRef.current = null;
//       }
//     };
//   // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [sessionId, userId, hasEnded]);

//   const handleAdminEndSessionWithOptions = async () => {
//     if (isEndingRef.current || hasEnded) return;
    
//     const result = await Swal.fire({
//       title: 'End Assessment Session?',
//       text: `Candidate has left. How would you like to proceed with ${selectedUser?.first_name || 'the candidate'}?`,
//       icon: 'question',
//       showCancelButton: true,
//       showDenyButton: true,
//       confirmButtonColor: '#10b981',
//       denyButtonColor: '#ef4444',
//       cancelButtonColor: '#6b7280',
//       confirmButtonText: '✅ Accept',
//       denyButtonText: '❌ Reject',
//       cancelButtonText: '⏳ Pending',
//       allowOutsideClick: false
//     });

//     try {
//       if (result.isConfirmed) {
//         await handleAdminAcceptCandidate();
//       } else if (result.isDenied) {
//         await handleAdminRejectCandidate();
//       } else if (result.isDismissed) {
//         await handleAdminPendingCandidate();
//       }
//     } catch (error) {
//       console.error('Error handling admin decision:', error);
//       toast.error('Failed to process decision');
//     } finally {
//       await cleanupAndClose(true);
//       onClose?.();
//     }
//   };

//   const handleAdminAcceptCandidate = async () => {
//     try {
//       const response = await axios.post(
//         `${config.baseURL}/api/military/sessions/${sessionId}/complete`,
//         {},
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
      
//       if (response.data.success) {
//         toast.success(`${selectedUser?.first_name || 'Candidate'} has been accepted and session completed!`);
//         onSessionComplete?.(response.data);
//       }
//     } catch (error) {
//       console.error('Error completing session:', error);
//       toast.error('Failed to complete session');
//       throw error;
//     }
//   };

//   const handleAdminRejectCandidate = async () => {
//     try {
//       await axios.post(
//         `${config.baseURL}/api/military/sessions/${sessionId}/abandon`,
//         { status: 'rejected', action: 'reject' },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
      
//       if (programId) {
//         await axios.patch(
//           `${config.baseURL}/api/military/programs/${programId}/candidate-status`,
//           { status: 'rejected' },
//           { headers: { Authorization: `Bearer ${token}` } }
//         );
//       }
      
//       toast.info(`${selectedUser?.first_name || 'Candidate'} has been rejected`);
//     } catch (error) {
//       console.error('Error rejecting candidate:', error);
//       toast.error('Failed to reject candidate');
//       throw error;
//     }
//   };

//   const handleAdminPendingCandidate = async () => {
//     try {
//       await axios.post(
//         `${config.baseURL}/api/military/sessions/${sessionId}/abandon`,
//         { status: 'pending', action: 'abandon' },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
      
//       toast.info(`${selectedUser?.first_name || 'Candidate'} marked as pending - session can be resumed later`);
//     } catch (error) {
//       console.error('Error marking as pending:', error);
//       toast.error('Failed to mark as pending');
//       throw error;
//     }
//   };

//   const handleEndSession = async () => {
//     if (isEndingRef.current || hasEnded) return;

//     if (isAdmin) {
//       const result = await Swal.fire({
//         title: 'End Assessment Session?',
//         text: `How would you like to proceed with ${selectedUser?.first_name || 'the candidate'}?`,
//         icon: 'question',
//         showCancelButton: true,
//         showDenyButton: true,
//         confirmButtonColor: '#10b981',
//         denyButtonColor: '#ef4444',
//         cancelButtonColor: '#6b7280',
//         confirmButtonText: '✅ Accept',
//         denyButtonText: '❌ Reject',
//         cancelButtonText: '⏳ Pending',
//         allowOutsideClick: false
//       });

//       try {
//         if (result.isConfirmed) {
//           await handleAdminAcceptCandidate();
//         } else if (result.isDenied) {
//           await handleAdminRejectCandidate();
//         } else if (result.isDismissed) {
//           await handleAdminPendingCandidate();
//         }
//       } catch (error) {
//         console.error('Error handling admin decision:', error);
//       } finally {
//         await cleanupAndClose(true);
//         onClose?.();
//       }
//     } else {
//       if (onAbandonSession) {
//         try {
//           await onAbandonSession(sessionId);
//         } catch (_) {}
//       }
//       await cleanupAndClose(false);
//       onClose?.();
//     }
//   };

//   // ─── Join military socket room ──────────────────────────────────────────────
//   useEffect(() => {
//     if (!socketRef.current || !sessionId) return;
//     const join = () => socketRef.current?.emit("join-military-room", {
//       sessionId,
//       userType: isAdmin ? "admin" : "candidate"
//     });
//     socketRef.current.connected ? join() : socketRef.current.on("connect", join);
//     return () => {
//       socketRef.current?.emit("leave-military-room", { sessionId });
//     };
//   // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [sessionId, isAdmin]);

//   // ─── Listen for question-answered events ────────────────────────────────────
//   useEffect(() => {
//     if (!socketRef.current) return;
//     const handler = (data) => {
//       if (String(data.sessionId) === String(sessionId)) {
//         setAnsweredCount(data.questionNumber);
//         loadSessionDetails();
//       }
//     };
//     socketRef.current.on("military-question-answered", handler);
//     return () => socketRef.current?.off("military-question-answered", handler);
//   // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [sessionId]);

//   // ─── Full cleanup on component unmount ──────────────────────────────────────
//   useEffect(() => {
//     return () => {
//       if (!hasEnded && !sessionCompleted && !hasAbandonedRef.current) {
//         callAbandonAPI(sessionId);
//       }

//       if (nextQuestionTimerRef.current) {
//         clearInterval(nextQuestionTimerRef.current);
//         nextQuestionTimerRef.current = null;
//       }

//       cleanupMediaAndPeer();

//       if (socketRef.current) {
//         socketRef.current.emit("leave-military-video", { sessionId: String(sessionId) });
//         socketRef.current.emit("leave-military-room", { sessionId });
//         socketRef.current.disconnect();
//         socketRef.current = null;
//       }
//     };
//   // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   // ─── Helpers ─────────────────────────────────────────────────────────────────

//   /** Complete cleanup of all media resources and peer connection */
//   const cleanupMediaAndPeer = () => {
//     if (cleanupInProgressRef.current) return;
//     cleanupInProgressRef.current = true;

//     // Clear all timers
//     if (nextQuestionTimerRef.current) {
//       clearInterval(nextQuestionTimerRef.current);
//       nextQuestionTimerRef.current = null;
//     }

//     // Stop and clear local stream
//     if (localStreamRef.current) {
//       localStreamRef.current.getTracks().forEach(track => {
//         track.stop();
//         track.enabled = false;
//       });
//       localStreamRef.current = null;
//     }

//     // Clear video elements
//     if (localVideoRef.current) {
//       if (localVideoRef.current.srcObject) {
//         localVideoRef.current.srcObject.getTracks?.().forEach(track => track.stop());
//         localVideoRef.current.srcObject = null;
//       }
//     }
    
//     if (remoteVideoRef.current) {
//       if (remoteVideoRef.current.srcObject) {
//         remoteVideoRef.current.srcObject.getTracks?.().forEach(track => track.stop());
//         remoteVideoRef.current.srcObject = null;
//       }
//     }

//     // Close peer connection
//     if (peerRef.current) {
//       try {
//         peerRef.current.getTransceivers?.().forEach(t => {
//           try { t.stop?.(); } catch (_) {}
//         });
//         peerRef.current.close();
//       } catch (_) {}
//       peerRef.current = null;
//     }

//     cleanupInProgressRef.current = false;
//   };

//   /** Call the abandon API exactly once */
//   const callAbandonAPI = async (sid) => {
//     if (hasAbandonedRef.current || !sid) return;
//     hasAbandonedRef.current = true;
//     try {
//       await axios.post(
//         `${config.baseURL}/api/military/sessions/${sid}/abandon`,
//         {},
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//     } catch (err) {
//       console.error('callAbandonAPI failed:', err);
//     }
//   };

//   /** Single exit point for all close paths */
//   const cleanupAndClose = useCallback(async (skipAbandon = false) => {
//     if (isEndingRef.current) return;
//     isEndingRef.current = true;

//     if (!skipAbandon && !sessionCompleted && !hasAbandonedRef.current) {
//       await callAbandonAPI(sessionId);
//     }

//     setShowQuestionPanel(false);
//     setWaitingForNext(false);
//     setCurrentQuestion(null);

//     cleanupMediaAndPeer();

//     if (socketRef.current) {
//       socketRef.current.emit("leave-military-video", { sessionId: String(sessionId) });
//       socketRef.current.emit("leave-military-room", { sessionId });
//       socketRef.current.disconnect();
//       socketRef.current = null;
//     }

//     setHasEnded(true);
//     setIsCallActive(false);
//     setIsConnected(false);
//   }, [sessionId, sessionCompleted]);

//   // ─── Data loading ───────────────────────────────────────────────────────────
//   const loadSessionDetails = async () => {
//     try {
//       const response = await axios.get(
//         `${config.baseURL}/api/military/sessions/${sessionId}`,
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//       if (response.data.success) {
//         const sd = response.data.session;
//         const sortedQuestions = [...sd.questions].sort((a, b) => a.id - b.id);
        
//         setQuestions(sortedQuestions);
//         setAnsweredCount(sd.answered_count);

//         if (!waitingForNext && !isAdmin) {
//           const firstUnansweredIdx = sortedQuestions.findIndex(q => !q.answered);
//           if (firstUnansweredIdx !== -1) {
//             setCurrentQuestionIndex(firstUnansweredIdx);
//             setCurrentQuestion(sortedQuestions[firstUnansweredIdx]);
//             setShowQuestionPanel(true);
//           } else if (sd.answered_count === sd.total_questions) {
//             setSessionCompleted(true);
//             setShowQuestionPanel(false);
//           }
//         }
//       }
//     } catch (error) {
//       console.error('Error loading session:', error);
//       toast.error('Failed to load session questions');
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const showNextQuestion = () => {
//     const nextIdx = currentQuestionIndex + 1;
//     if (nextIdx < questions.length) {
//       setCurrentQuestionIndex(nextIdx);
//       setCurrentQuestion(questions[nextIdx]);
//       setShowQuestionPanel(true);
//       setWaitingForNext(false);
//       setNextQuestionTime(null);
//     } else {
//       completeSession();
//     }
//   };

//   // ─── WebRTC ─────────────────────────────────────────────────────────────────
//   const startCall = async () => {
//     try {
//       const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
//       if (localVideoRef.current) localVideoRef.current.srcObject = stream;
//       localStreamRef.current = stream;

//       socketRef.current.emit("join-military-video", { sessionId: String(sessionId) });
//       createPeerConnection();
//       stream.getTracks().forEach(track => peerRef.current?.addTrack(track, stream));

//       setTimeout(async () => {
//         if (peerRef.current?.signalingState === "stable") {
//           const offer = await peerRef.current.createOffer();
//           await peerRef.current.setLocalDescription(offer);
//           socketRef.current.emit("military-video-offer", { sessionId: String(sessionId), offer });
//           setIsCallActive(true);
//         }
//       }, 1000);
//     } catch (err) {
//       setError(`Failed to access camera/microphone: ${err.message}`);
//     }
//   };

//   const createPeerConnection = () => {
//     peerRef.current = new RTCPeerConnection({
//       iceServers: [
//         { urls: "stun:stun.l.google.com:19302" },
//         { urls: "stun:stun1.l.google.com:19302" },
//         { urls: "stun:stun2.l.google.com:19302" },
//       ],
//     });

//     peerRef.current.onicecandidate = (event) => {
//       if (event.candidate) {
//         socketRef.current?.emit("military-ice-candidate", {
//           sessionId: String(sessionId),
//           candidate: event.candidate
//         });
//       }
//     };

//     peerRef.current.ontrack = (event) => {
//       if (remoteVideoRef.current) {
//         remoteVideoRef.current.srcObject = event.streams[0];
//       }
//     };
//   };

//   const setupSocketListeners = (socket) => {
//     socket.on("military-video-offer", async ({ offer }) => {
//       if (!peerRef.current) createPeerConnection();
//       await peerRef.current.setRemoteDescription(new RTCSessionDescription(offer));
//       const answer = await peerRef.current.createAnswer();
//       await peerRef.current.setLocalDescription(answer);
//       socket.emit("military-video-answer", { sessionId: String(sessionId), answer });
//       setIsCallActive(true);
//     });

//     socket.on("military-video-answer", async ({ answer }) => {
//       if (peerRef.current && peerRef.current.remoteDescription === null) {
//         await peerRef.current.setRemoteDescription(new RTCSessionDescription(answer));
//       }
//     });

//     socket.on("military-ice-candidate", async ({ candidate }) => {
//       if (peerRef.current && candidate) {
//         await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate));
//       }
//     });
//   };

//   // ─── Answer submission (verbal only - no text input) ────────────────────────
//   const handleSubmitAnswer = async () => {
//     if (isSubmitting) return;
//     setIsSubmitting(true);

//     try {
//       const response = await axios.post(
//         `${config.baseURL}/api/military/sessions/${sessionId}/submit-response`,
//         {
//           question_id: currentQuestion.id,
//           answer_text: "[VERBAL RESPONSE - RECORDED VIA VIDEO]",
//           video_url: null,
//           reaction_time: null
//         },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       if (response.data.success) {
//         const newCount = answeredCount + 1;
//         setAnsweredCount(newCount);
//         setShowQuestionPanel(false);

//         socketRef.current?.emit("military-question-answered", {
//           sessionId: String(sessionId),
//           questionId: currentQuestion.id,
//           questionNumber: newCount,
//           totalQuestions: questions.length
//         });

//         socketRef.current?.emit("military-answer-update", {
//           sessionId: String(sessionId),
//           questionId: currentQuestion.id,
//           answerText: "[VERBAL RESPONSE - RECORDED VIA VIDEO]",
//           questionNumber: newCount,
//           totalQuestions: questions.length
//         });

//         const nextIdx = currentQuestionIndex + 1;
//         if (nextIdx < questions.length) {
//           toast.success(`Question ${newCount} of ${questions.length} completed`);
//           setCurrentQuestionIndex(nextIdx);
//           setCurrentQuestion(questions[nextIdx]);
//           setWaitingForNext(true);
//           setNextQuestionTime(Date.now() + 120000); // 2 minute wait before next question
//         } else {
//           await completeSession();
//         }
//       }
//     } catch (error) {
//       console.error('Error submitting answer:', error);
//       toast.error('Failed to submit answer');
//       setShowQuestionPanel(true);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // ─── Session completion ─────────────────────────────────────────────────────
//   const completeSession = async () => {
//     try {
//       const response = await axios.post(
//         `${config.baseURL}/api/military/sessions/${sessionId}/complete`,
//         {},
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//       if (response.data.success) {
//         hasAbandonedRef.current = true;
//         setSessionCompleted(true);
//         setSessionResults(response.data);
//         setShowQuestionPanel(false);
//         setWaitingForNext(false);

//         if (response.data.program_completed) {
//           toast.success('🎉 Assessment Program Completed!');
//         } else {
//           toast.success(`Week ${weekNumber} completed!`);
//         }
//         onSessionComplete?.(response.data);
//       }
//     } catch (error) {
//       console.error('Error completing session:', error);
//       toast.error('Failed to complete session');
//     }
//   };

//   // ─── Audio / video toggles ──────────────────────────────────────────────────
//   const toggleMute = () => {
//     if (localStreamRef.current) {
//       const audioTracks = localStreamRef.current.getAudioTracks();
//       audioTracks.forEach(track => { track.enabled = !track.enabled; });
//       setIsMuted(prev => !prev);
//     }
//   };

//   const toggleVideo = () => {
//     if (localStreamRef.current) {
//       const videoTracks = localStreamRef.current.getVideoTracks();
//       videoTracks.forEach(track => { track.enabled = !track.enabled; });
//       setIsVideoOff(prev => !prev);
//     }
//   };

//   const formatTime = (seconds) => {
//     const m = Math.floor(seconds / 60);
//     const s = seconds % 60;
//     return `${m}:${s.toString().padStart(2, '0')}`;
//   };

//   // ─── Early returns ──────────────────────────────────────────────────────────
//   if (hasEnded) return null;

//   if (error) {
//     return (
//       <div className="military_video-call-error">
//         <p>{error}</p>
//         <button onClick={handleEndSession}>Close</button>
//       </div>
//     );
//   }

//   // ─── Minimized view ─────────────────────────────────────────────────────────
//   if (isMinimized) {
//     return (
//       <div className="military_video-call-minimized">
//         <div className="military_minimized-header">
//           <span className="military_minimized-title">Week {weekNumber} Assessment</span>
//           <button className="military_minimized-maximize-btn" onClick={onToggleMinimize}>
//             <FaWindowMaximize />
//           </button>
//         </div>
//         <div className="military_minimized-video">
//           <video ref={localVideoRef} autoPlay muted playsInline />
//           <div className="military_minimized-label">
//             You {waitingForNext && <span className="military_waiting-indicator"> ⏳</span>}
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // ─── Admin view ─────────────────────────────────────────────────────────────
//   if (isAdmin) {
//     return (
//       <div className="military_video-call-container">
//         <div className="military_video-call-header">
//           <div className="military_header-title">
//             Week {weekNumber} Assessment: {selectedUser?.first_name || 'Candidate'}
//           </div>
//           <div className="military_header-actions">
//             <button className="military_minimize-btn" onClick={onToggleMinimize}>
//               <FaWindowMinimize />
//             </button>
//           </div>
//         </div>

//         <div className="military_video-grid">
//           <div className="military_video-local">
//             <video ref={localVideoRef} autoPlay muted playsInline />
//             <div className="military_video-label">You</div>
//           </div>
//           <div className="military_video-remote">
//             <video ref={remoteVideoRef} autoPlay playsInline />
//             <div className="military_video-label">{selectedUser?.first_name || 'Candidate'}</div>
//           </div>
//         </div>

//         <div className="military_video-controls">
//           <button onClick={toggleMute} className={`military_control-btn ${isMuted ? 'military_active' : ''}`}>
//             {isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
//           </button>
//           <button onClick={toggleVideo} className={`military_control-btn ${isVideoOff ? 'military_active' : ''}`}>
//             {isVideoOff ? <FaVideoSlash /> : <FaVideo />}
//           </button>
//           <button onClick={() => setShowAdminQuestions(true)} className="military_control-btn military_questions-btn">
//             <HiOutlineClipboardList />
//           </button>
//           <button onClick={handleEndSession} className="military_control-btn military_end-btn">
//             <MdCallEnd />
//           </button>
//         </div>

//         {showAdminQuestions && (
//           <MilitaryAdminQuestionsView
//             sessionId={sessionId}
//             token={token}
//             weekNumber={weekNumber}
//             candidateName={selectedUser?.first_name}
//             onClose={() => setShowAdminQuestions(false)}
//           />
//         )}
//       </div>
//     );
//   }

//   // ─── Candidate loading view ─────────────────────────────────────────────────
//   if (isLoading) {
//     return (
//       <div className="military_video-call-container">
//         <div className="military_video-call-header">
//           <div className="military_header-title">Week {weekNumber} Assessment</div>
//           <div className="military_header-actions">
//             <button className="military_minimize-btn" onClick={onToggleMinimize}>
//               <FaWindowMinimize />
//             </button>
//           </div>
//         </div>
//         <div className="military_video-grid">
//           <div className="military_video-local">
//             <video ref={localVideoRef} autoPlay muted playsInline />
//             <div className="military_video-label">You</div>
//           </div>
//           <div className="military_video-remote">
//             <video ref={remoteVideoRef} autoPlay playsInline />
//             <div className="military_video-label">Interviewer</div>
//           </div>
//         </div>
//         <div className="military_loading-overlay">
//           <div className="military_loading-spinner"></div>
//           <h3>Preparing your assessment session...</h3>
//         </div>
//       </div>
//     );
//   }

//   // ─── Candidate completed view ───────────────────────────────────────────────
//   if (sessionCompleted) {
//     return (
//       <div className="military_video-call-container">
//         <div className="military_video-call-header">
//           <div className="military_header-title">Week {weekNumber} Assessment</div>
//           <div className="military_header-actions">
//             <button className="military_minimize-btn" onClick={onToggleMinimize}>
//               <FaWindowMinimize />
//             </button>
//           </div>
//         </div>
//         <div className="military_video-grid">
//           <div className="military_video-local">
//             <video ref={localVideoRef} autoPlay muted playsInline />
//             <div className="military_video-label">You</div>
//           </div>
//           <div className="military_video-remote">
//             <video ref={remoteVideoRef} autoPlay playsInline />
//             <div className="military_video-label">Interviewer</div>
//           </div>
//         </div>
//         <div className="military_completion-panel">
//           <div className="military_completion-icon">✅</div>
//           <h3>Week {weekNumber} Complete!</h3>
//           <p>You've successfully completed this week's assessment.</p>
//           {sessionResults?.program_completed ? (
//             <div className="military_program-complete">
//               <h4>🎉 Program Completed!</h4>
//               <p>Your full assessment results are ready.</p>
//             </div>
//           ) : (
//             <p>Next session will be available in 7 days.</p>
//           )}
//           <button
//             className="military_close-btn"
//             onClick={async () => {
//               await cleanupAndClose(true);
//               onClose?.();
//             }}
//           >
//             Close
//           </button>
//         </div>
//       </div>
//     );
//   }

//   // ─── Candidate active view (verbal-only interview) ──────────────────────────
//   return (
//     <div className="military_video-call-container">
//       <div className="military_video-call-header">
//         <div className="military_header-title">Week {weekNumber} Assessment</div>
//         <div className="military_header-actions">
//           <button className="military_minimize-btn" onClick={onToggleMinimize}>
//             <FaWindowMinimize />
//           </button>
//         </div>
//       </div>

//       <div className="military_video-grid">
//         <div className="military_video-local">
//           <video ref={localVideoRef} autoPlay muted playsInline />
//           <div className="military_video-label">
//             You
//             {isMuted    && <FaMicrophoneSlash className="military_status-icon" />}
//             {isVideoOff && <FaVideoSlash      className="military_status-icon" />}
//           </div>
//         </div>
//         <div className="military_video-remote">
//           <video ref={remoteVideoRef} autoPlay playsInline />
//           <div className="military_video-label">Interviewer</div>
//         </div>
//       </div>

//       {/* Question panel - verbal only, no textarea */}
//       {showQuestionPanel && currentQuestion && !waitingForNext && !sessionCompleted && (
//                 <div className="military_question-panel">
//           <div className="military_question-header">
//             <div className="military_question-progress">
//               Question {currentQuestionIndex + 1} of {questions.length}
//             </div>
//           </div>
//           <div className="military_question-content">
//             <div className="military_question-category">{currentQuestion.category}</div>
//             <div className="military_question-text">{currentQuestion.text}</div>
//           </div>
//           <div className="military_answer-section">
//             <div className="military_action-buttons">
//               <button
//                 className="military_skip-btn"
//                 onClick={() => handleSubmitAnswer('[SKIPPED]')}
//                 disabled={isSubmitting}
//               >
//                 Skip
//               </button>
//               <button
//                 className="military_submit-btn"
//                 onClick={() => handleSubmitAnswer()}
//                 // disabled={isSubmitting || !answerText.trim()}
//               >
//                 {isSubmitting ? 'Submitting...' : 'Submit Answer'}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Waiting panel - 2 minute break between questions */}
//       {waitingForNext && nextQuestionTime && !showQuestionPanel && !sessionCompleted && (
//         <div className="military_waiting-panel">
//           <div className="military_waiting-icon">⏳</div>
//           <h3>Next Question in {formatTime(waitTimeRemaining)}</h3>
//           <p>Question {currentQuestionIndex + 1} of {questions.length}</p>
//           <div className="military_waiting-progress">
//             <div
//               className="military_waiting-progress-fill"
//               style={{ width: `${((120 - waitTimeRemaining) / 120) * 100}%` }}
//             />
//           </div>
//           <p className="military_waiting-hint">Take a moment to prepare for the next question</p>
//         </div>
//       )}

//       <div className="military_video-controls">
//         <button onClick={toggleMute} className={`military_control-btn ${isMuted ? 'military_active' : ''}`}>
//           {isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
//         </button>
//         <button onClick={toggleVideo} className={`military_control-btn ${isVideoOff ? 'military_active' : ''}`}>
//           {isVideoOff ? <FaVideoSlash /> : <FaVideo />}
//         </button>
//         <button onClick={handleEndSession} className="military_control-btn military_end-btn">
//           <MdCallEnd />
//         </button>
//       </div>
//     </div>
//   );
// };

// export default MilitarySessionVideoCall;